// ResumeUpload.js
import React, { useState, useRef } from "react";
import { uploadToCloudinary } from "./cloudinaryService";
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf";
import mammoth from "mammoth";
import { analyzeResume } from "./aiService";
import { toast } from "react-hot-toast";

// Set PDF.js worker — matching the installed version (5.6.205)
// Version 5.x uses .mjs for its worker by default on most CDNs
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@5.6.205/build/pdf.worker.min.mjs`;

// ─── Extract text from PDF ───
async function extractPdfText(file) {
  try {
    const arrayBuffer = await file.arrayBuffer();
    
    // Robust configuration with CMaps (critical for many PDF types)
    const loadingTask = pdfjsLib.getDocument({
      data: arrayBuffer,
      cMapUrl: "https://unpkg.com/pdfjs-dist@5.6.205/cmaps/",
      cMapPacked: true,
      standardFontDataUrl: "https://unpkg.com/pdfjs-dist@5.6.205/standard_fonts/",
    });
    
    const pdf = await loadingTask.promise;
    let fullText = "";
    console.log(`[PDF Extraction] Pages: ${pdf.numPages}`);
    
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const content = await page.getTextContent();
      
      // Filter out empty items and join with proper spacing
      const pageText = content.items
        .map((item) => {
          // Some items have .str, some might have different structure in newer versions
          return item.str || "";
        })
        .filter(str => str.trim().length > 0)
        .join(" ");
      
      fullText += pageText + "\n";
      console.log(`[PDF Extraction] Page ${i} read. Length: ${pageText.length}`);
    }
    
    const finalizedText = fullText.trim();
    if (finalizedText.length < 50) {
      console.warn("[PDF Extraction] Suspiciously short text. Possible scanned PDF or encoding issue.");
    } else {
      console.log("[PDF Extraction] Success. Total length:", finalizedText.length);
    }
    
    return finalizedText;
  } catch (err) {
    console.error("[PDF Extraction] Critical Error:", err);
    throw err;
  }
}

// ─── Extract text from DOCX ───
async function extractDocxText(file) {
  const arrayBuffer = await file.arrayBuffer();
  const result = await mammoth.extractRawText({ arrayBuffer });
  return result.value;
}

// ─── ResumeUpload Component ───
function ResumeUpload({ userId, onResumeProcessed, jobDescription = "" }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState(""); // idle, uploading, extracting, done, error
  const [extractedText, setExtractedText] = useState("");
  const fileInputRef = useRef(null);

  const acceptedTypes = [
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ];

  const handleFileSelect = (e) => {
    const selected = e.target.files[0];
    if (!selected) return;
    if (!acceptedTypes.includes(selected.type)) {
      alert("Please upload a PDF, DOC, or DOCX file.");
      return;
    }
    if (selected.size > 10 * 1024 * 1024) {
      alert("File size must be under 10MB.");
      return;
    }
    setFile(selected);
    setStatus("");
    setProgress(0);
  };

  const handleUploadAndProcess = async () => {
    if (!file) return alert("Please select a resume file first.");

    try {
      setUploading(true);
      setProgress(50); // Start progress immediately
      setStatus("extracting");

      // --- STEP 1: Extract Text (Instant & Local) ---
      console.log("Step 1: Extracting text locally...");
      let finalExtractedText = "";
      try {
        if (file.type === "application/pdf") {
          finalExtractedText = await extractPdfText(file);
        } else if (file.type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document") {
          finalExtractedText = await extractDocxText(file);
        } else {
          finalExtractedText = (await file.text?.()) || "Extraction error";
        }
      } catch (extError) {
        console.warn("Extraction failed, but proceeding anyway:", extError);
        finalExtractedText = "Manual reading required (Extraction failed)";
      }
      setExtractedText(finalExtractedText);

      // --- STEP 1.5: AI Analysis (Evaluation) ---
      console.log("Step 1.5: Running AI Resume Analysis...");
      const analysis = await analyzeResume(finalExtractedText, jobDescription);
      console.log("AI Analysis complete:", analysis.resumeScore);

      // --- STEP 2 & 4: Resume Cloud Sync (Resumable) ---
      const uid = userId || "anonymous";
      const folderName = `resumes/${uid}`;

      console.log(`Starting Resume Sync to Cloudinary folder: ${folderName}`);
      setStatus("uploading");
      setUploading(true);

      // --- STEP 3: Initial State (Optional) ---
      if (onResumeProcessed) {
        onResumeProcessed({
          ...analysis,
          resumeText: finalExtractedText,
          isInstantExtract: true 
        });
      }

      const downloadUrl = await uploadToCloudinary(file, folderName, (prog) => {
          setProgress(prog);
          console.log(`Resume Upload Progress: ${prog}%`);
      }, "auto");

      console.log("✅ Resume Sync Complete:", downloadUrl);
      
      if (onResumeProcessed) {
        onResumeProcessed({
          ...analysis,
          resumeUrl: downloadUrl,
          resumeFileName: file.name,
          resumeText: finalExtractedText,
          isCompleteUpload: true
        });
      }
      setStatus("done");
      setProgress(100);
      setUploading(false);
      toast.success("Resume finalized and secured!");

    } catch (error) {
      console.error("Resume Upload Logic Error:", error);
      setStatus("error");
      setUploading(false);
      toast.error(error.message || "Resume file could not reach the cloud. Please check your connection.");
    } finally {
      // setUploading(false); // Handled in individual callbacks now
    }
  };

  const getStatusLabel = () => {
    switch (status) {
      case "uploading": return "Uploading...";
      case "extracting": return "Processing file...";
      case "done": return "Resume ready!";
      case "error": return "Upload error.";
      default: return "";
    }
  };

  return (
    <div className="resume-upload-container">
      <div className="resume-upload-header">
        <div className="resume-icon">📄</div>
        <h3>Upload Resume</h3>
        <p>Your resume will be linked to your interview profile</p>
      </div>

      <div
        className={`resume-dropzone ${file ? "has-file" : ""}`}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.doc,.docx"
          onChange={handleFileSelect}
          style={{ display: "none" }}
        />
        {file ? (
          <div className="file-selected">
            <span className="file-icon">
              {file.name.endsWith(".pdf") ? "📕" : "📘"}
            </span>
            <div className="file-info">
              <span className="file-name">{file.name}</span>
              <span className="file-size">
                {(file.size / 1024).toFixed(1)} KB
              </span>
            </div>
          </div>
        ) : (
          <div className="dropzone-content">
            <span className="upload-icon">☁️</span>
            <p>Select PDF / DOCX file</p>
          </div>
        )}
      </div>

      {status === "uploading" && (
        <div className="upload-progress-section">
          <div className="progress-bar-track">
            <div
              className="progress-bar-fill-resume"
              style={{ width: `${progress}%` }}
            />
          </div>
          <span className="progress-text">{progress}%</span>
        </div>
      )}

      {status && (
        <div className={`resume-status ${status}`}>
          {getStatusLabel()}
        </div>
      )}

      {status === "done" && (
        <div className="resume-success-node" style={{ textAlign: "center", padding: "10px", color: "#10b981", fontWeight: "600" }}>
          ✅ Resume upload successful. You can now start the interview.
        </div>
      )}

      <button
        className="resume-upload-btn"
        onClick={handleUploadAndProcess}
        disabled={!file || uploading || status === "done"}
      >
        {status === "extracting" ? "🔍 Processing..." :
         status === "uploading" ? "📤 Uploading..." :
         status === "done" ? "✓ Upload Complete" : 
         uploading ? "Uploading..." : "Upload Resume & Proceed"}
      </button>

      {status === "error" && (
        <div className="manual-entry-fallback" style={{ marginTop: "15px", padding: "10px", border: "1px dashed #ef4444", borderRadius: "8px" }}>
          <p style={{ fontSize: "0.9rem", color: "#ef4444" }}>
            <strong>Upload failed.</strong> Please try another file or refresh the page.
          </p>
        </div>
      )}
    </div>
  );
}

export default ResumeUpload;
